import { useState, useEffect } from 'react';
import { Check } from "lucide-react";
import { useSelector, useDispatch } from 'react-redux';
import * as actions from '../redux/actions/billing';
import { BILLING_COMPLETE_EVENT } from '../helper/billingReturn';

/** Main workspace seat price from plan.seatPrices (falls back to plan.price). */
const getMainSeatPrice = (plan) => {
    if (!plan) return null;
    const main = (plan.seatPrices || []).find((p) => p.seatRole === "main");
    return main?.price ?? plan.price;
};

export const PricingPlanModal = ({ isOpen, onClose, returnUrl, artistProfileId }) => {
    const dispatch = useDispatch();
    const [isAnnual, setIsAnnual] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { subscriptionPlan } = useSelector(state => state.billing);

    useEffect(() => {
        const closeOnBillingReturn = () => onClose?.();
        window.addEventListener(BILLING_COMPLETE_EVENT, closeOnBillingReturn);
        return () => window.removeEventListener(BILLING_COMPLETE_EVENT, closeOnBillingReturn);
    }, [onClose]);

    useEffect(() => {
        if (!isOpen) return;
        dispatch(actions.getPlanList("subscription", undefined, "main"));
    }, [dispatch, isOpen]);

    if (!isOpen) return null;

    // Get monthly and yearly plans
    const monthlyPlan = subscriptionPlan?.find((plan) => plan.interval === "monthly");
    const yearlyPlan = subscriptionPlan?.find((plan) => plan.interval === "yearly");
    const activePlan = isAnnual ? yearlyPlan : monthlyPlan;
    const displayPrice = getMainSeatPrice(activePlan);

    const handleCheckout = async (planId) => {
        if (!planId || isLoading) return;
        const opened = await dispatch(actions.generateStripeSubscriptionCheckout({
            planId,
            returnUrl: returnUrl || actions.getBillingReturnUrl(),
            seatType: "main",
            artistProfileId,
        }, setIsLoading));
        // Close immediately so the page behind isn't blocked while Stripe is open or after return.
        if (opened !== false) onClose?.();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm overflow-auto">
            {/* Modal content */}
            <div className="bg-gradient-to-br from-[#000456] via-[#0a1a8a] to-[#1e3a8a] rounded-2xl shadow-lg w-full max-w-[1000px] mx-2 my-4 animate-fadeIn">

                {/* Body */}
                <div className="modal-body relative px-4 py-8">
                    <button onClick={onClose} className="absolute top-3 end-4 text-theme_clr2 hover:text-theme_clr4 text-xl transition"><i className="bi bi-x-lg lh-1"></i></button>
                    <div className="text-center mb-12 mt-2">
                        <div className="font-extrabold text-3xl text-white mb-2">🔥Replace a $10K/month team — for just <span className="text-theme_clr2">${displayPrice}/ {isAnnual ? "year" : "month"}</span></div>
                        <p className="text-lg text-gray-300 mb-4 max-w-3xl mx-auto">
                            Get the power of a full label team while keeping 100% creative and financial control.
                        </p>

                        {/* Billing Toggle */}
                        <div className="bg-gray-200 flex items-center justify-center max-w-[300px] mx-auto p-1 rounded-full">
                            <span onClick={() => setIsAnnual(!isAnnual)} className={`text-sm font-semibold ${!isAnnual ? "bg-theme_clr2 text-white" : "text-theme_clr1"} flex-1 p-2 rounded-full`}>Monthly</span>
                            {/* <button onClick={() => setIsAnnual(!isAnnual)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isAnnual ? "bg-[#FA6138]" : "bg-gray-600"}`}>
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isAnnual ? "translate-x-6" : "translate-x-1"}`} />
                            </button> */}
                            <span onClick={() => setIsAnnual(!isAnnual)} className={`text-sm font-semibold ${isAnnual ? "bg-theme_clr2 text-white" : "text-theme_clr1"} flex-1 p-2 rounded-full`}>Annual</span>
                        </div>
                    </div>

                    <div className="flex justify-center items-center gap-8">
                        <div className="relative bg-white/5 backdrop-blur-sm border rounded-xl px-4 md:px-8 py-8 border-[#FA6138]/50 shadow-[0_0_30px_rgba(250,97,56,0.2)] hover:border-[#FA6138]/50 transition-all duration-300 max-w-2xl w-full">
                            {/* Badge */}
                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                                <span className="bg-[#FA6138] text-white text-xs font-bold px-3 py-1 rounded-full">MOST POPULAR</span>
                            </div>

                            {/* Header */}
                            {activePlan && (
                                <div className="text-center mb-8">
                                    <h3 className="text-3xl font-bold text-white mb-2">🟧 {activePlan?.name}</h3>
                                    <p className="text-gray-300 mb-4">{activePlan?.description}</p>

                                    {/* Pricing */}
                                    <div className="space-y-1">
                                        <div className="text-4xl font-bold text-[#FA6138]">${displayPrice}<span className="text-xl text-gray-300 font-normal"> / {isAnnual ? "year" : "month"}</span>{isAnnual && <span className="text-xl font-normal text-[#FA6138]"> (Save 2 months)</span>}</div>
                                        {/* <div className="text-sm text-green-400">🆓 Create your account — no credit card required</div> */}
                                    </div>
                                    {/* CTA Button */}
                                    <button
                                        disabled={isLoading} // disable while processing
                                        className={`w-full py-4 mt-6 rounded-lg font-bold text-lg text-white transition-all duration-300 ${isLoading
                                            ? "bg-gray-400 cursor-not-allowed"
                                            : "bg-[#FA6138] hover:bg-[#e55532]"
                                            }`}

                                        onClick={() => handleCheckout(activePlan.id)}
                                    >
                                        {isLoading ? (
                                            <div className="flex items-center justify-center gap-2">
                                                <svg
                                                    className="animate-spin h-5 w-5 text-white"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <circle
                                                        className="opacity-25"
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="4"
                                                    ></circle>
                                                    <path
                                                        className="opacity-75"
                                                        fill="currentColor"
                                                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                                    ></path>
                                                </svg>
                                                <span>Processing...</span>
                                            </div>
                                        ) : (
                                            <span>BECOME AN UPNCOMER →</span>
                                        )}

                                    </button>
                                </div>
                            )}

                            {/* Features */}
                            <div className="mb-8">
                                <h4 className="text-lg font-semibold text-white mb-4">🎯 Includes:</h4>
                                <ul className="space-y-3">
                                    {[
                                        "Growth Engine — Run smart, goal-driven ad campaigns",
                                        "Audio Engineer — Master your music professionally (AI Mastering)",
                                        "Amplitude AI — Predictive intelligence across your career",
                                        "Data Analyst — Streaming & audience analytics",
                                        "Artist Manager — Contracts, revenues, smart links",
                                        "Publicist (coming soon) — Press coverage & label tracking",
                                        "ProfessorU (coming soon) — Industry education & trend spotting",
                                        "Content Creator (coming soon) — Create & schedule content",
                                        "Support — Live chat & email",
                                        "1 Artist Profile — All tools apply to a single artist account",
                                    ].map((feature, i) => (
                                        <li key={i} className="flex items-start space-x-3">
                                            <Check className="w-5 h-5 text-[#FA6138] flex-shrink-0 mt-0.5" />
                                            <span className="text-gray-300 text-sm">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Bottom Section */}
                            <div className="text-center space-y-3">
                                <p className="text-gray-300">
                                    <span className="font-semibold text-white">🔶 Become an UpNComer</span><br />
                                    Everything you need to scale — for less than the cost of {isAnnual ? "a studio session." : "your t-shirt."}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

